import { LEAD_STATUSES, type LeadStatusKey, type LeadsSummaryResponse } from '@/api/types';
import { formatMoney, formatPercent } from './format';
import { Funnel, type FunnelStage } from './Funnel';

/** Human labels for the stage vocabulary. The server owns the order; this owns the wording. */
export const STATUS_LABELS: Record<LeadStatusKey, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  won: 'Won',
  lost: 'Lost',
};

/**
 * Stage chip. Won and lost are the only two that carry colour — they are the outcomes, and
 * tinting the working stages as well would leave nothing for the eye to land on.
 */
export function LeadStatusChip({ status }: { status: LeadStatusKey }) {
  const className =
    status === 'won' ? 'chip chip-won' : status === 'lost' ? 'chip chip-lost' : 'chip chip-stage';

  return <span className={className}>{STATUS_LABELS[status] ?? status}</span>;
}

/**
 * The pipeline, as a funnel plus the outcome pair beside it.
 *
 * <b>The stages are CUMULATIVE, not per-status counts.</b> "Contacted" here means everyone who
 * reached contacted or beyond, so each stage is a superset of the next and the bars fall
 * monotonically. Plotting raw per-status counts in a funnel is the classic version of this chart
 * that lies: a healthy week that closes deals empties the earlier stages, and the funnel would
 * draw that success as a collapse.
 *
 * Lost is deliberately NOT a funnel stage — a lead can be lost from any stage, so it has no
 * position in an ordered sequence. It sits beside the funnel as an outcome instead.
 */
export function LeadPipeline({ summary }: { summary: LeadsSummaryResponse }) {
  const count = (status: LeadStatusKey) => summary.by_status[status] ?? 0;

  // Rank each stage so "reached at least here" is a comparison, not a chain of additions.
  const rank: Record<LeadStatusKey, number> = { new: 0, contacted: 1, qualified: 2, won: 3, lost: -1 };
  const reached = (minimum: LeadStatusKey) =>
    LEAD_STATUSES.filter((s) => rank[s] >= rank[minimum] && rank[s] >= 0)
      .reduce((sum, s) => sum + count(s), 0);

  const stages: FunnelStage[] = [
    { label: 'Captured', value: summary.total, note: 'Every lead in range, both sources' },
    { label: 'Contacted', value: reached('contacted') },
    { label: 'Qualified', value: reached('qualified') },
    { label: 'Won', value: count('won') },
  ];

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Pipeline</h2>
        <span className="t-label subtle">cumulative — each stage includes the ones after it</span>
      </div>

      <div className="card-body">
        <Funnel stages={stages} />

        <div className="lead-outcomes">
          <Outcome
            label="Won"
            value={`${summary.won_count.toLocaleString('en-US')}`}
            sub={formatMoney(summary.won_value, summary.currency)}
            tone="good"
          />
          <Outcome
            label="Lost"
            value={`${summary.lost_count.toLocaleString('en-US')}`}
            sub={summary.lost_count > 0 ? 'closed without a deal' : 'none yet'}
            tone="bad"
          />
          <Outcome
            label="Win rate"
            value={formatPercent(summary.win_rate)}
            sub="of decided leads"
          />
          <Outcome
            label="Still open"
            value={`${(summary.total - summary.won_count - summary.lost_count).toLocaleString('en-US')}`}
            sub={formatMoney(summary.pipeline_value, summary.currency)}
          />
        </div>
      </div>
    </div>
  );
}

function Outcome({
  label, value, sub, tone = 'neutral',
}: { label: string; value: string; sub?: string; tone?: 'neutral' | 'good' | 'bad' }) {
  const color =
    tone === 'good' ? 'var(--success)' : tone === 'bad' ? 'var(--error)' : 'var(--text-main)';

  return (
    <div className="lead-outcome">
      <div className="t-label muted">{label}</div>
      <div className="t-heading" style={{ color }}>{value}</div>
      {sub && <div className="t-caption subtle">{sub}</div>}
    </div>
  );
}
